import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import '../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import 'tippy.js/dist/tippy.css'; // optional
import 'tippy.js/themes/light.css';
import './scss/style.scss';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import TheLayout from './containers';
import { AUTH_PATH, GUEST_PATH } from './constants';
import { CSpinner } from '@coreui/react-pro';
import 'react-calendar/dist/Calendar.css';
import { getBrowser, initializeGTM } from './utils';
import { isAuth } from './helpers/auth';

dayjs.extend(utc);
dayjs.extend(timezone);

const Login = React.lazy(() => import('./components/Auth/Login'));
const Register = React.lazy(() => import('./components/Auth/Register'));
const PasswordForgot = React.lazy(() => import('./components/Auth/PasswordForgot'));
const PasswordReset = React.lazy(() => import('./components/Auth/PasswordReset'));
const Unsubscribe = React.lazy(() => import('./components/Auth/Unsubscribe'));
const ConfirmAlternateEmail = React.lazy(() => import('./components/CMS/Guest/ConfirmAlternateEmail'));

const loading = () => (
     <div className="page-loading-container">
          <CSpinner color="primary" />
     </div>
);

const App = () => {
     const classBody = getBrowser();
     const isDevelopmentEnv = process.env.REACT_APP_ENV === 'development';
     const isStagingEnv = process.env.REACT_APP_ENV === 'staging' && window.location.hostname.includes('.hostedstaging.com');
     const isProd = process.env.REACT_APP_ENV === 'prod';

     if (isDevelopmentEnv || isStagingEnv || isProd) {
          initializeGTM();
     }

     const Head = () => (
          <Helmet bodyAttributes={{ class: classBody }}>
               <meta charSet="utf-8" />
               <meta name="viewport" content="width=device-width, initial-scale=1" />
               <title>Suicidal Ideation Treatment Training & Certifications | Cams Care</title>
               <meta
                    name="description"
                    content="CAMS Care offers expert training in the CAMS Framework® for effective suicidal ideation treatment. Choose from CAMS-Trained™, Advanced, or CAMS-Certified™ courses."
               />
               <link rel="canonical" href="https://training.cams-care.com/" />
               <meta property="og:locale" content="en_US" />
               <meta property="og:type" content="website" />
               <meta property="og:title" content="Suicidal Ideation Treatment Training & Certifications | Cams Care" />
               <meta
                    property="og:description"
                    content="CAMS Care offers expert training in the CAMS Framework® for effective suicidal ideation treatment. Choose from CAMS-Trained™, Advanced, or CAMS-Certified™ courses."
               />
               <meta property="og:url" content="https://cams-care.com/" />
               <meta property="og:site_name" content="CAMS-Care" />

               <meta name="twitter:card" content="summary_large_image" />
               <meta name="twitter:site" content="@CAMScare_tweets" />

               <link rel="preconnect" href="https://fonts.googleapis.com" />
               <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
               <link
                    rel="stylesheet"
                    href="https://pro.fontawesome.com/releases/v5.14.0/css/all.css"
                    integrity="sha384-VhBcF/php0Z/P5ZxlxaEx1GwqTQVIBu4G4giRWxTKOCjTxsPFETUDdVL5B6vYvOt"
                    crossorigin="anonymous"
               />
               <link
                    href="https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400..700;1,400..700&family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap"
                    rel="stylesheet"
               />
               <link
                    href="https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400&display=swap"
                    rel="stylesheet"
               />
               <link
                    href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
                    rel="stylesheet"
               />
               <link rel="stylesheet" href="https://use.typekit.net/szj0jra.css" />
          </Helmet>
     );

     return (
          <BrowserRouter>
               <Head />
               <React.Suspense fallback={loading()}>
                    <Routes>
                         <Route exact path={AUTH_PATH.LOGIN} element={!isAuth() ? <Login /> : <Navigate to="/" replace />} />
                         <Route exact path={AUTH_PATH.REGISTER} element={!isAuth() ? <Register /> : <Navigate to="/" replace />} />
                         <Route exact path={AUTH_PATH.PASSWORD_FORGOT} element={<PasswordForgot />} />
                         <Route exact path={AUTH_PATH.PASSWORD_RESET} element={<PasswordReset />} />
                         <Route path={AUTH_PATH.UNSUBSCRIBE_EMAIL} element={<Unsubscribe />} />
                         <Route path={GUEST_PATH.CONFIRM_ALTERNATE_EMAIL} element={<ConfirmAlternateEmail />} />
                         <Route path="/*" element={<TheLayout />} />
                    </Routes>

                    {/* Make toast function of react-toastify work in all components */}
                    <ToastContainer />
               </React.Suspense>
          </BrowserRouter>
     );
};

export default App;
